import type { ViewTransitionDirection } from '../../state/appUiStore'
import { activateAdjacentTab } from './tabNavigationActions'

const SWIPE_MIN_DISTANCE_PX = 64
const SWIPE_AXIS_DOMINANCE_RATIO = 1.35
const SWIPE_EXCLUDED_TARGET_SELECTOR =
   'input, textarea, select, option, [contenteditable=""], [contenteditable="true"]'

type SwipeGesture = {
   pointerId: number
   startX: number
   startY: number
   currentX: number
   currentY: number
}

type ClosestTarget = {
   closest: (selector: string) => unknown
}

export type TabSwipeNavigationHandlers = {
   handlePointerDown: (event: PointerEvent) => void
   handlePointerMove: (event: PointerEvent) => void
   handlePointerUp: (event: PointerEvent) => void
   resetSwipeGesture: () => void
}

/**
 * Builds touch/pen swipe handlers for ordered tab navigation.
 *
 * @returns Pointer handlers for the view stack.
 */
export function useTabSwipeNavigation(): TabSwipeNavigationHandlers {
   let swipeGesture: SwipeGesture | null = null

   /**
    * Returns whether a pointer started on a text-entry or native form target.
    *
    * @param target - The pointer event target.
    * @returns True when the target should keep full ownership of the gesture.
    */
   const isSwipeExcludedTarget = (target: EventTarget | null): boolean => {
      const closestTarget = target as unknown as Partial<ClosestTarget> | null
      return Boolean(
         closestTarget &&
         typeof closestTarget.closest === 'function' &&
         closestTarget.closest(SWIPE_EXCLUDED_TARGET_SELECTOR),
      )
   }

   /**
    * Clears the active swipe gesture.
    */
   const resetSwipeGesture = () => {
      swipeGesture = null
   }

   /**
    * Resolves the tab direction from the active swipe gesture.
    *
    * @returns The tab direction, or null when the gesture is not horizontal enough.
    */
   const resolveSwipeDirection = (): ViewTransitionDirection | null => {
      if (!swipeGesture) {
         return null
      }

      const deltaX = swipeGesture.currentX - swipeGesture.startX
      const deltaY = swipeGesture.currentY - swipeGesture.startY
      const horizontalDistance = Math.abs(deltaX)
      const verticalDistance = Math.abs(deltaY)
      if (
         horizontalDistance < SWIPE_MIN_DISTANCE_PX ||
         horizontalDistance < verticalDistance * SWIPE_AXIS_DOMINANCE_RATIO
      ) {
         return null
      }

      return deltaX < 0 ? 'forward' : 'backward'
   }

   /**
    * Starts tracking a touch or pen gesture.
    *
    * @param event - The pointer-down event.
    */
   const handlePointerDown = (event: PointerEvent) => {
      if (
         !event.isPrimary ||
         (event.pointerType !== 'touch' && event.pointerType !== 'pen') ||
         isSwipeExcludedTarget(event.target)
      ) {
         return
      }

      swipeGesture = {
         pointerId: event.pointerId,
         startX: event.clientX,
         startY: event.clientY,
         currentX: event.clientX,
         currentY: event.clientY,
      }
   }

   /**
    * Updates the active gesture with the latest pointer position.
    *
    * @param event - The pointer-move event.
    */
   const handlePointerMove = (event: PointerEvent) => {
      if (!swipeGesture || swipeGesture.pointerId !== event.pointerId) {
         return
      }

      swipeGesture = {
         ...swipeGesture,
         currentX: event.clientX,
         currentY: event.clientY,
      }
   }

   /**
    * Finishes the active gesture and activates an adjacent tab when appropriate.
    *
    * @param event - The pointer-up event.
    */
   const handlePointerUp = (event: PointerEvent) => {
      if (!swipeGesture || swipeGesture.pointerId !== event.pointerId) {
         return
      }

      const direction = resolveSwipeDirection()
      resetSwipeGesture()
      if (direction) {
         event.preventDefault()
         activateAdjacentTab(direction)
      }
   }

   return {
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      resetSwipeGesture,
   }
}
